import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Select, Upload, 
  message, Space, Popconfirm, InputNumber, Card, Divider,
  Typography, Row, Col
} from 'antd';
import { 
  UploadOutlined, PlusOutlined, EditOutlined, DeleteOutlined, 
  ExclamationCircleOutlined, FileExcelOutlined 
} from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { 
  QuestionBank, 
  QuestionBankCreateRequest, 
  QuestionBankUpdateRequest,
  Course,
  Subject
} from '../../../../shared/types/database';
import { questionBankService } from '../../services/questionBankService';
import courseService from '../../services/courseService';
import * as subjectService from '../../services/subjectService';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const QuestionBankManagementPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [questions, setQuestions] = useState<QuestionBank[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [importModalVisible, setImportModalVisible] = useState<boolean>(false);
  const [editingQuestion, setEditingQuestion] = useState<QuestionBank | null>(null);
  const [form] = Form.useForm();
  const [importForm] = Form.useForm();

  // Lấy danh sách khóa học khi component được mount
  useEffect(() => {
    fetchCourses();
  }, []);

  // Lấy danh sách môn học khi khóa học thay đổi
  useEffect(() => {
    if (selectedCourse) {
      fetchSubjects(selectedCourse);
      setSelectedSubject(null);
      setQuestions([]);
    } else {
      setSubjects([]);
      setSelectedSubject(null);
      setQuestions([]);
    }
  }, [selectedCourse]);

  // Lấy danh sách câu hỏi khi môn học thay đổi
  useEffect(() => {
    if (selectedSubject) {
      fetchQuestions(selectedSubject);
    } else {
      setQuestions([]);
    }
  }, [selectedSubject]);

  // Lấy danh sách khóa học từ API
  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await courseService.getCourses();
      setCourses(response.data);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách khóa học:', error);
      message.error('Không thể lấy danh sách khóa học');
    } finally {
      setLoading(false);
    }
  };

  // Lấy danh sách môn học từ API theo khóa học
  const fetchSubjects = async (courseId: string) => {
    try {
      setLoading(true);
      console.log('Fetching subjects for course:', courseId);
      const response = await subjectService.getSubjectsByCourse(courseId);
      console.log('Subjects response:', response);
      setSubjects(response);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách môn học:', error);
      console.error('Error details:', error);
      message.error('Không thể lấy danh sách môn học: ' + (error as Error).message);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  };

  // Lấy danh sách câu hỏi từ API theo môn học
  const fetchQuestions = async (subjectId: string) => {
    try {
      setLoading(true);
      const response = await questionBankService.getQuestions(subjectId);
      setQuestions(response);
    } catch (error) {
      console.error('Lỗi khi lấy danh sách câu hỏi:', error);
      message.error('Không thể lấy danh sách câu hỏi: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Mở modal thêm/sửa câu hỏi
  const showModal = (question?: QuestionBank) => {
    setEditingQuestion(question || null);
    if (question) {
      form.setFieldsValue({
        MonHocID: question.MonHocID,
        NoiDungCauHoi: question.NoiDungCauHoi,
        ThoiGianDuKien: question.ThoiGianDuKien
      });
    } else {
      form.setFieldsValue({
        MonHocID: selectedSubject,
        NoiDungCauHoi: '',
        ThoiGianDuKien: 15
      });
    }
    setModalVisible(true);
  };

  // Đóng modal thêm/sửa câu hỏi
  const handleCancel = () => {
    setModalVisible(false);
    setEditingQuestion(null);
    form.resetFields();
  };

  // Xử lý submit form thêm/sửa câu hỏi
  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      
      if (editingQuestion) {
        // Cập nhật câu hỏi
        await questionBankService.updateQuestion(editingQuestion.CauHoiID, values);
        message.success('Cập nhật câu hỏi thành công');
      } else {
        // Thêm câu hỏi mới
        await questionBankService.createQuestion(values);
        message.success('Thêm câu hỏi thành công');
      }
      
      setModalVisible(false);
      setEditingQuestion(null);
      form.resetFields();
      
      // Lấy lại danh sách câu hỏi
      if (selectedSubject) {
        fetchQuestions(selectedSubject);
      }
    } catch (error) {
      console.error('Lỗi khi lưu câu hỏi:', error);
      message.error('Không thể lưu câu hỏi: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Xử lý xóa câu hỏi
  const handleDelete = async (questionId: number) => {
    try {
      setLoading(true);
      await questionBankService.deleteQuestion(questionId);
      message.success('Xóa câu hỏi thành công');
      
      // Lấy lại danh sách câu hỏi
      if (selectedSubject) {
        fetchQuestions(selectedSubject);
      }
    } catch (error) {
      console.error('Lỗi khi xóa câu hỏi:', error);
      message.error('Không thể xóa câu hỏi: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Mở modal import
  const showImportModal = () => {
    importForm.setFieldsValue({
      MonHocID: selectedSubject
    });
    setImportModalVisible(true);
  };

  // Đóng modal import
  const handleImportCancel = () => {
    setImportModalVisible(false);
    importForm.resetFields();
  };

  // Xử lý import từ Excel
  const handleImport = async (values: any) => {
    try {
      if (!values.file || values.file.fileList.length === 0) {
        message.error('Vui lòng chọn file Excel');
        return;
      }
      
      const file = values.file.fileList[0].originFileObj;
      
      setLoading(true);
      const response = await questionBankService.importQuestions(values.MonHocID, file);
      
      message.success(`Import thành công ${response.successCount} câu hỏi`);
      if (response.errorCount > 0) {
        message.warning(`Có ${response.errorCount} câu hỏi không import được`);
      }
      
      setImportModalVisible(false);
      importForm.resetFields();
      
      // Lấy lại danh sách câu hỏi
      if (selectedSubject) {
        fetchQuestions(selectedSubject);
      }
    } catch (error) {
      console.error('Lỗi khi import câu hỏi:', error);
      message.error('Không thể import câu hỏi: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Cấu hình các cột của bảng
  const columns: ColumnsType<QuestionBank> = [
    {
      title: 'STT',
      key: 'index',
      width: 60,
      render: (_, __, index) => index + 1,
      align: 'center'
    },
    {
      title: 'Nội dung câu hỏi',
      dataIndex: 'NoiDungCauHoi',
      key: 'NoiDungCauHoi',
      render: (text: string) => {
        if (text && text.length > 150) {
          return (
            <div>
              {text.substring(0, 150)}...
              <Button type="link" size="small">
                Xem thêm
              </Button>
            </div>
          );
        }
        return text;
      },
    },
    {
      title: 'Thời gian (phút)',
      dataIndex: 'ThoiGianDuKien',
      key: 'ThoiGianDuKien',
      width: 120,
      align: 'center',
      sorter: (a, b) => a.ThoiGianDuKien - b.ThoiGianDuKien
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'NgayTao',
      key: 'NgayTao',
      width: 120,
      render: (text: Date) => dayjs(text).format('DD/MM/YYYY'),
      sorter: (a, b) => dayjs(a.NgayTao).unix() - dayjs(b.NgayTao).unix()
    },
    {
      title: 'Người tạo',
      dataIndex: 'NguoiTao',
      key: 'NguoiTao',
      width: 120,
    },
    {
      title: 'Hành động',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            ghost
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
            size="small"
          />
          <Popconfirm
            title="Xóa câu hỏi"
            description="Bạn có chắc chắn muốn xóa câu hỏi này?"
            okText="Có"
            cancelText="Không"
            icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            onConfirm={() => handleDelete(record.CauHoiID)}
          >
            <Button type="primary" danger ghost icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
      align: 'center'
    },
  ];

  // Các tùy chọn upload
  const uploadProps = {
    beforeUpload: (file: File) => {
      const isExcel = 
        file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
        file.type === 'application/vnd.ms-excel';
      
      if (!isExcel) {
        message.error('Chỉ hỗ trợ file Excel (.xlsx, .xls)!');
      }
      
      return false;
    },
  };

  return (
    <div className="question-bank-page">
      <Card title="Quản lý Ngân hàng Câu Hỏi" bordered={false}>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Form.Item label="Khóa học">
              <Select
                placeholder="Chọn khóa học"
                value={selectedCourse}
                onChange={setSelectedCourse}
                allowClear
                loading={loading}
              >
                {courses.map(course => (
                  <Option key={course.KhoaHocID} value={course.KhoaHocID}>
                    {course.TenKhoaHoc}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item label="Môn học">
              <Select
                placeholder="Chọn môn học"
                value={selectedSubject}
                onChange={setSelectedSubject}
                disabled={!selectedCourse}
                allowClear
                loading={loading}
              >
                {subjects.map(subject => (
                  <Option key={subject.MonHocID} value={subject.MonHocID}>
                    {subject.TenMonHoc}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          
          <Col span={8}>
            {selectedSubject && (
              <div style={{ marginTop: 30 }}>
                <Text strong>Tổng số câu hỏi: {questions.length}</Text>
              </div>
            )}
          </Col>
        </Row>
        
        <Divider />
        
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
              disabled={!selectedSubject}
            >
              Thêm câu hỏi
            </Button>
            
            <Button
              type="primary"
              icon={<UploadOutlined />}
              onClick={showImportModal}
              disabled={!selectedSubject}
            >
              Import từ Excel
            </Button>
          </Space>
        </div>
        
        <Table
          columns={columns}
          dataSource={questions}
          rowKey="CauHoiID"
          loading={loading}
          pagination={{ 
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} câu hỏi`
          }}
          scroll={{ x: 1000 }}
        />
      </Card>
      
      {/* Modal thêm/sửa câu hỏi */}
      <Modal
        title={editingQuestion ? "Sửa câu hỏi" : "Thêm câu hỏi mới"}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="MonHocID"
            label="Môn học"
            rules={[{ required: true, message: 'Vui lòng chọn môn học' }]}
          >
            <Select 
              placeholder="Chọn môn học"
              disabled={!!editingQuestion || !!selectedSubject}
            >
              {subjects.map(subject => (
                <Option key={subject.MonHocID} value={subject.MonHocID}>
                  {subject.TenMonHoc}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="NoiDungCauHoi"
            label="Nội dung câu hỏi"
            rules={[{ required: true, message: 'Vui lòng nhập nội dung câu hỏi' }]}
          >
            <TextArea rows={6} placeholder="Nhập nội dung câu hỏi..." />
          </Form.Item>
          
          <Form.Item
            name="ThoiGianDuKien"
            label="Thời gian dự kiến (phút)"
            rules={[
              { required: true, message: 'Vui lòng nhập thời gian dự kiến' },
              {
                validator: (_, value) => {
                  if (value && (value <= 0 || value % 5 !== 0)) {
                    return Promise.reject('Thời gian phải là bội số của 5 (5, 10, 15, 20, ...)');
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <InputNumber min={5} step={5} style={{ width: '100%' }} />
          </Form.Item>
          
          <Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={handleCancel} style={{ marginRight: 8 }}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingQuestion ? 'Cập nhật' : 'Thêm mới'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
      
      {/* Modal import từ Excel */}
      <Modal
        title="Import câu hỏi từ Excel"
        open={importModalVisible}
        onCancel={handleImportCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <Title level={5}>Hướng dẫn định dạng file Excel:</Title>
          <ul>
            <li>Cột 1: <strong>Nội dung câu hỏi</strong> (bắt buộc)</li>
            <li>Cột 2: <strong>Thời gian dự kiến (phút)</strong> - Phải là bội số của 5 (5, 10, 15, 20, 25, 30...)</li>
          </ul>
          <Text type="secondary">
            Lưu ý: Dòng đầu tiên có thể là tiêu đề hoặc dữ liệu thực tế
          </Text>
        </div>
        
        <Form
          form={importForm}
          layout="vertical"
          onFinish={handleImport}
        >
          <Form.Item
            name="MonHocID"
            label="Môn học"
            rules={[{ required: true, message: 'Vui lòng chọn môn học' }]}
          >
            <Select
              placeholder="Chọn môn học"
              disabled={!!selectedSubject}
            >
              {subjects.map(subject => (
                <Option key={subject.MonHocID} value={subject.MonHocID}>
                  {subject.TenMonHoc}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="file"
            label="File Excel"
            rules={[{ required: true, message: 'Vui lòng chọn file Excel' }]}
          >
            <Upload {...uploadProps} maxCount={1}>
              <Button icon={<UploadOutlined />}>Chọn file Excel</Button>
            </Upload>
          </Form.Item>
          
          <Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Button onClick={handleImportCancel} style={{ marginRight: 8 }}>
                Hủy
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                Import
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QuestionBankManagementPage;
